import React from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import styled from "@emotion/styled";

//style rules
import { Sizes, Animation, Colors, Typography } from "../../styles";

const StaffModal = ({ toggleStaffModal, staffMember }) => {

	const renderImage = () => {
		if (!staffMember.photo) {
			return (
				<ImageContainer>
					<img
						src={
							mainScript.assetPath + "images/png/graphics/people-placeholder.png"
						}
						alt={"Staff member card placeholder"}
					/>
				</ImageContainer>
			);
		}

		return (
			<ImageContainer>
				<img src={staffMember.photo} alt={`Image for ${staffMember.title}`} />
			</ImageContainer>
		);
	};

	return (
		<Overlay
			className="backdrop"
			variants={Animation.modal.overlayVariants}
			initial="hidden"
			animate="visible"
			exit="hidden"
			onClick={() => toggleStaffModal()}
		>
			<Modal
				variants={Animation.modal.modalVariants}
				onClick={(e) => e.stopPropagation()}
				role="dialog"
			>
				<CloseIcon
					icon={mainScript.assetPath + "images/svg/icons/icon-close-green.svg"}
					onClick={() => toggleStaffModal()}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							toggleStaffModal(staffMember);
						}
					}}
					aria-label="Close modal"
					autofocus
				/>
				<FlexContainer>
					<LeftSide>{renderImage()}</LeftSide>
					<RightSide>
						<Name dangerouslySetInnerHTML={{ __html: staffMember.name }} />
						{staffMember.job_title && (
							<SubTitle>{staffMember.job_title}</SubTitle>
						)}
						{staffMember.professional_title && (
							<CardMeta>{staffMember.professional_title}</CardMeta>
						)}
						{staffMember.location && (
							<CardMeta>{staffMember.location}</CardMeta>
						)}
						<Socials>
							{staffMember.facebook && (
								<SocialIcon
									href={staffMember.facebook}
									aria-label={"Facebook icon"}
									target="_blank"
								>
									<img
										src={
											mainScript.assetPath +
											`images/svg/logos/facebook-green.svg`
										}
										alt={`${staffMember.name} Facebook`}
									/>
								</SocialIcon>
							)}
							{staffMember.twitter && (
								<SocialIcon
									href={staffMember.twitter}
									aria-label={"Twitter icon"}
									target="_blank"
								>
									<img
										src={mainScript.assetPath + `images/svg/logos/x-green.svg`}
										alt={`${staffMember.name} Twitter`}
									/>
								</SocialIcon>
							)}
							{staffMember.linkedin && (
								<SocialIcon
									href={staffMember.linkedin}
									aria-label={"Instagram icon"}
									target="_blank"
								>
									<img
										src={
											mainScript.assetPath +
											`images/svg/logos/linkedin-green.svg`
										}
										alt={`${staffMember.name} linkedIn`}
									/>
								</SocialIcon>
							)}
							{staffMember.website && (
								<SocialIcon
									href={staffMember.website}
									aria-label={"Instagram icon"}
									target="_blank"
								>
									<img
										src={
											mainScript.assetPath +
											`images/svg/logos/website-link-green.svg`
										}
										alt={`${staffMember.name} website`}
									/>
								</SocialIcon>
							)}
						</Socials>
						{staffMember.bio && (
							<Bio dangerouslySetInnerHTML={{ __html: staffMember.bio }} />
						)}
					</RightSide>
				</FlexContainer>
			</Modal>
		</Overlay>
	);
};

export default StaffModal;

StaffModal.propTypes = {
	staffMember: PropTypes.object,
	toggleStaffModal: PropTypes.func,
};

const Overlay = styled(motion.div)`
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background: #00000050;
	z-index: 1000;
	cursor: pointer;
`;

const Modal = styled(motion.div)`
	position: relative;
	width: calc(100% - 10px);
	height: 90%;
	margin: 3.3rem auto;
	padding: 9rem 3rem;
	background: white;
	cursor: default;
	border-radius: 20px;
	overflow: scroll;

	@media only screen and (min-width: ${Sizes.screens.large}) {
		overflow: scroll;
		padding: 8rem;
		width: unset;
		max-width: ${Sizes.grids.maxWidth};
	}
`;

const FlexContainer = styled.div`
	display: flex;
	gap: 4rem;
	flex-direction: column;
	align-items: center;

	@media only screen and (min-width: ${Sizes.screens.large}) {
		flex-direction: row;
		gap: 8rem;
		align-items: flex-start;
	}
`;

const LeftSide = styled.div`
	display: flex;
	flex-direction: column;
	max-width: 500px;
	width: 100%;

	@media only screen and (min-width: ${Sizes.screens.large}) {
		max-width: 366px;
	}
`;

const ImageContainer = styled.div`
	height: 0;
	padding-top: calc(366 / 366 * 100%);
	position: relative;
	width: 100%;
	border-radius: 20px;
	overflow: hidden;
	transition: all 0.5s ease-in-out;
	border-radius: 20px;

	img {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
`;

const Name = styled.h3`
	margin-bottom: 1rem;
`;

const SubTitle = styled.p`
	font-size: 1.5rem;
	line-height: 1.9rem;
	font-weight: ${Typography.fontWeight.bold};
	display: block;
	color: ${Colors.base.black};
	margin-bottom: 1.5rem;
`;

const CardMeta = styled.p`
	font-size: 1.5rem;
	line-height: 1.9rem;
	font-weight: ${Typography.fontWeight.medium};
	margin-bottom: 2rem;
`;

const Socials = styled.div`
	display: flex;
	align-items: center;
	gap: 3rem;
	margin: 3rem 0;
`;

const SocialIcon = styled.a`
	&:after {
		content: unset;
	}

	&:hover,
	&:focus {
		transition: all 0.3s;
		transform: translateY(-5px);
	}
`;

const RightSide = styled.div`
	max-width: 664px;

	@media only screen and (min-width: ${Sizes.screens.large}) {
		// height: 550px;
		overflow: scroll;
	}
`;
const Bio = styled.div`
	p:not(:last-child) {
		margin-bottom: 3rem;
	}
`;

const CloseIcon = styled.button`
	content: "";
	background: url(${(props) => props.icon && props.icon}) no-repeat;
	width: 30px;
	height: 30px;
	margin: 0.4rem 0 0 0.5rem;
	cursor: pointer;
	position: absolute;
	top: 36px;
	right: 36px;
	padding: 0;
	transition: transform 0.3s ease-in-out;

	&::after {
		content: none;
	}

	&:hover,
	&:focus {
		padding-right: 0;
		transition: none;
		transform: scale(1.5);

		&::after {
			content: none;
		}
	}
`;
